import {useEffect, useState} from "react";
import {Artist, convertArtistToType} from "../../../utils/artist.utils";
import {useValidateCodeMutation} from "../logic/queries/code.queries";
import {useClearSessionMutation} from "../../../services/session/logic/queries/session.queries";
import {SessionAPI} from "../../../services/session/logic/api/session.api";
import {SESSION_STATUS} from "../../../services/session/logic/types/session.type";
import {useSessionStore} from "../../../services/session/store";
import {isServiceUnavailableError} from "../../../utils/error.utils";
import {CHARACTER_STORAGE_KEY} from "src/services/chat/storage/characterStorage";

const ERROR_MESSAGE = {
  INVALID: "invalid",
  SESSION_ALIVE: "sessionAlive",
  TOO_MANY_FAILED_ATTEMPTS_IN_SESSION: "tooManyFailedAttemptsInSession",
  TOO_MANY_FAILED_ATTEMPTS_IN_IP: "tooManyFailedAttemptsInIp",
  ABNORMAL_AUTHENTICATION_ATTEMPTS_IP: "abnormalAuthenticationAttemptsIp",
  UNKNOWN: "unknown",
} as const;

type UseCodeParams = {
  artist: Artist;
  onForegroundSessionNone?: () => void;
  onTermsRequired: () => void;
  onExpired: () => void;
  onServiceUnavailable: () => void;
};

export const useCode = ({
  artist,
  onForegroundSessionNone,
  onTermsRequired,
  onExpired,
  onServiceUnavailable,
}: UseCodeParams) => {
  const [code, setCode] = useState<string>("");
  const [error, setError] = useState<string>("");

  const validateCodeMutation = useValidateCodeMutation();
  const clearSessionMutation = useClearSessionMutation();

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    if (error) {
      setError("");
    }
  };

  const handleDelete = () => {
    setCode("");
    if (error) {
      setError("");
    }
  };

  const handleClearSession = () => {
    // API 요청만 호출. 에러 발생 시 무시.
    clearSessionMutation.mutateAsync().catch(() => {});
  };

  const handleSubmit = async (options?: {terms_agreed?: boolean}) => {
    try {
      if (!code.trim()) {
        setError(ERROR_MESSAGE.INVALID);
        return;
      }
      setError("");

      const type = convertArtistToType(artist);

      const parsedCode = code.trim();

      const response = await validateCodeMutation.mutateAsync({
        data: {
          type,
          code: parsedCode,
          ...(options?.terms_agreed && {termsAgreed: options.terms_agreed}),
        },
      });

      localStorage.removeItem(CHARACTER_STORAGE_KEY);

      return response;
    } catch (error) {
      console.log(error);
      if (isServiceUnavailableError(error)) {
        onServiceUnavailable();
        return;
      }
      const errorMessage = (error as Error).message;

      if (
        errorMessage ===
        "Code entry limit exceeded. Please try again after 30 minutes."
      ) {
        // 세션 기준 누적 10회 실패 시
        setError(ERROR_MESSAGE.TOO_MANY_FAILED_ATTEMPTS_IN_SESSION);
        return;
      }
      if (
        errorMessage ===
        "Code entry limit exceeded. Please try again after 10 minutes."
      ) {
        // IP 기준 1분 내 5회 이상 실패 시
        setError(ERROR_MESSAGE.TOO_MANY_FAILED_ATTEMPTS_IN_IP);
        return;
      }
      if (
        errorMessage ===
        "Restricted due to unusual activity. Please contact support."
      ) {
        // IP 기준 1분 내 15회 이상 실패 시
        setError(ERROR_MESSAGE.ABNORMAL_AUTHENTICATION_ATTEMPTS_IP);
        return;
      }

      if (errorMessage === "This is an invalid code. Please try again.") {
        setError(ERROR_MESSAGE.INVALID);
        return;
      }
      if (errorMessage === "This code is expired") {
        onExpired();
        return;
      }
      if (
        errorMessage ===
        "Re-entry is only allowed within 5 minutes of being in the chat room. Please start again from the queue."
      ) {
        setError(ERROR_MESSAGE.SESSION_ALIVE);
        return;
      }
      if (
        errorMessage === "Terms agreement is required before using this coupon."
      ) {
        onTermsRequired();
        return;
      }
      setError(ERROR_MESSAGE.UNKNOWN);
      return;
    }
  };

  // 포그라운드 재진입 감지 (모바일 백그라운드 복귀 + PC 탭 전환 복귀)
  const handleVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState === "hidden") {
      console.log("[useCode] 백그라운드 진입 감지");
      return;
    }

    console.log("[useCode] 포그라운드 진입 감지 → 세션 상태 확인");

    const type = convertArtistToType(artist);
    try {
      const {status, ttl} = await SessionAPI.fetchSessionStatus({
        query: {type},
      });

      if (status === SESSION_STATUS.NONE) {
        console.log("[useCode] 세션 상태: NONE → waiting 페이지로 이동");
        onForegroundSessionNone?.();
        return;
      }

      if (status === SESSION_STATUS.CODE) {
        console.log("[useCode] 세션 상태: CODE → 남은 시간 갱신");
        useSessionStore.getState().setSessionRemainingSeconds(ttl);
        return;
      }

      console.log(`[useCode] 세션 상태: ${status} (발생할 수 없는 상황)`);
    } catch (error) {
      if (isServiceUnavailableError(error)) {
        onServiceUnavailable();
        return;
      }
      console.error("[useCode] 세션 상태 조회 실패", error);
    }
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [onForegroundSessionNone]);

  return {
    code,
    error,
    ERROR_MESSAGE,
    handleCodeChange,
    handleDelete,
    handleSubmit,
    handleClearSession,
  };
};
